import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Map as ReactMapGL, MapRef, Point } from "react-map-gl/mapbox";
import { getItemTypeColor } from "../utils/colors";
import {
  MapFilter,
  MapMarkerData,
  ListingNewWithDetailsDict,
  ListingWithDetails,
} from "../types";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapMarker } from "./MapMarker";
import { MapFilterControls } from "./MapFilter";
import { LocationData } from "../types";
import { MapArcPath } from "./MapArcPath";

interface MapProps {
  listingItemsDict: ListingNewWithDetailsDict;
  selectedItem: ListingWithDetails | null;
  hoveredItem?: ListingWithDetails | null;
  onItemSelect: (item: ListingWithDetails | null) => void;
  onItemHover?: (item: ListingWithDetails | null) => void;
  locations?: Record<string, LocationData>;
  mapMarkers: MapMarkerData[];
}

const Map: React.FC<MapProps> = ({
  listingItemsDict,
  selectedItem,
  hoveredItem,
  onItemSelect,
  onItemHover,
  mapMarkers,
}) => {
  // STATE
  const [mapFilter, setMapFilter] = useState<MapFilter>("all");
  const [hoveredMarker, setHoveredMarker] = useState<MapMarkerData | null>(
    null
  );
  const [viewState, setViewState] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 3,
  });

  // VARS
  const mapboxToken =
    process.env.VITE_MAPBOX_ACCESS_TOKEN || "pk.your_token_here";

  // MEMOS
  const filteredMapMarkers = useMemo(() => {
    return mapMarkers.filter((item) =>
      mapFilter === "all" ? true : item.itemType === mapFilter
    );
  }, [mapMarkers, mapFilter]);

  // State to force re-render of SVG arcs when map moves
  const [, forceUpdate] = useState({});

  // Find connected marker IDs when hovering
  const connectedMarkerIds = useMemo(() => {
    if (!hoveredMarker) return new Set<string>();

    const hoveredListingItem = listingItemsDict[hoveredMarker.listingItemId];
    if (!hoveredListingItem) return new Set<string>();

    const connectedIds = new Set<string>();
    hoveredListingItem.connections.forEach((conn) => {
      // Find all markers for this connected item
      const connectedMarkers = mapMarkers.filter(
        (m) =>
          m.listingItemId === conn.subjectListingId ||
          m.listingItemId === conn.objectListingId
      );
      connectedMarkers.forEach((m) => connectedIds.add(m.id));
    });

    return connectedIds;
  }, [hoveredMarker, listingItemsDict, mapMarkers]);

  // REFS
  const mapRef = useRef<MapRef | null>(null);

  // callback ref: runs when React attaches the ref (later than your first effect if needed)
  const setMapRef = useCallback((instance: MapRef | null) => {
    mapRef.current = instance;
  }, []);

  // EFFECTS
  // Listen to map move events to keep SVG arcs synchronized
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !selectedItem) return;

    const handleMove = () => forceUpdate({});
    map.on("move", handleMove);

    return () => {
      map.off("move", handleMove);
    };
  }, [selectedItem, mapRef.current]);

  // When a card is clicked, zoom to bbox of item + connected items
  useEffect(() => {
    if (!selectedItem || !mapRef.current) return;

    // Find all markers for this selected item
    const itemMarkers = mapMarkers.filter(
      (marker) => marker.listingItemId === selectedItem.id
    );

    // Find all markers for connected items
    const connectedMarkers: MapMarkerData[] = [];
    selectedItem.connections.forEach((conn) => {
      const markers = mapMarkers.filter((m) => m.listingItemId === conn.id);
      connectedMarkers.push(...markers);
    });

    // Combine all markers (selected + connected)
    const allMarkers = [...itemMarkers, ...connectedMarkers];

    if (allMarkers.length === 0) return;

    const map = mapRef.current.getMap();

    if (allMarkers.length === 1 && connectedMarkers.length === 0) {
      // Single location with no connections: fly to it
      const marker = allMarkers[0];
      map.flyTo({
        center: [marker.coordinates.lng, marker.coordinates.lat],
        zoom: 9,
        duration: 2000,
      });
    } else {
      // Multiple locations or has connections: calculate bbox and fit to it
      const lngs = allMarkers.map((m) => m.coordinates.lng);
      const lats = allMarkers.map((m) => m.coordinates.lat);

      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 100,
          duration: 2000,
        }
      );
    }
  }, [selectedItem?.id, mapMarkers, listingItemsDict]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Map Filter Controls */}
      <MapFilterControls
        selectedFilter={mapFilter}
        onFilterChange={(filter) => setMapFilter(filter || "all")}
      />

      <ReactMapGL
        ref={setMapRef}
        {...viewState}
        onMove={(evt: any) => setViewState(evt.viewState)}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/elsewheredreams/cm9pqmhzc013d01r3hnw66a87"
        mapboxAccessToken={mapboxToken}
      >
        {/* Unified ListingItem markers */}
        {filteredMapMarkers.map((marker, index) => {
          const isHovered = hoveredMarker?.id === marker.id;
          const isConnected = connectedMarkerIds.has(marker.id);

          return (
            <MapMarker
              key={index}
              longitude={marker.coordinates.lng}
              latitude={marker.coordinates.lat}
              color={getItemTypeColor(marker.itemType)}
              isSelected={
                selectedItem?.id === marker.listingItemId &&
                selectedItem?.itemType === marker.itemType
              }
              isHovered={isHovered || isConnected}
              onClick={() => {
                onItemSelect(listingItemsDict[marker.listingItemId]);
              }}
              onMouseEnter={() => {
                setHoveredMarker(marker);
                onItemHover?.(listingItemsDict[marker.listingItemId]);
              }}
              onMouseLeave={() => {
                setHoveredMarker(null);
                onItemHover?.(null);
              }}
            />
          );
        })}
      </ReactMapGL>

      {/* Tooltips overlay */}
      {hoveredMarker && mapRef.current && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {/* Main hovered marker tooltip */}
          {(() => {
            const map = mapRef.current?.getMap();
            const bounds = map?.getBounds();
            if (!bounds) return null;

            // Check if marker is within visible bounds
            const isInBounds =
              hoveredMarker.coordinates.lng >= bounds.getWest() &&
              hoveredMarker.coordinates.lng <= bounds.getEast() &&
              hoveredMarker.coordinates.lat >= bounds.getSouth() &&
              hoveredMarker.coordinates.lat <= bounds.getNorth();

            if (!isInBounds) return null;

            const point = mapRef.current?.project([
              hoveredMarker.coordinates.lng,
              hoveredMarker.coordinates.lat,
            ]);
            if (!point) return null;

            return (
              <div
                style={{
                  position: "absolute",
                  left: point.x,
                  top: point.y - 30,
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                {hoveredMarker.name}
              </div>
            );
          })()}

          {/* Connected markers tooltips (faded) */}
          {Array.from(connectedMarkerIds).map((connectedId) => {
            const connectedMarker = mapMarkers.find(
              (m) => m.id === connectedId
            );
            if (!connectedMarker) return null;

            const map = mapRef.current?.getMap();
            const bounds = map?.getBounds();
            if (!bounds) return null;

            // Check if marker is within visible bounds
            const isInBounds =
              connectedMarker.coordinates.lng >= bounds.getWest() &&
              connectedMarker.coordinates.lng <= bounds.getEast() &&
              connectedMarker.coordinates.lat >= bounds.getSouth() &&
              connectedMarker.coordinates.lat <= bounds.getNorth();

            if (!isInBounds) return null;

            const point = mapRef.current?.project([
              connectedMarker.coordinates.lng,
              connectedMarker.coordinates.lat,
            ]);
            if (!point) return null;

            return (
              <div
                key={connectedId}
                style={{
                  position: "absolute",
                  left: point.x,
                  top: point.y - 30,
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  opacity: 0.5,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                {connectedMarker.name}
              </div>
            );
          })}
        </div>
      )}

      {/* Connection arcs overlay */}
      {selectedItem && mapRef.current && (
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <defs>
            {selectedItem.connections.map((connection, index) => {
              const targetItem: ListingWithDetails =
                listingItemsDict[connection.id];
              if (!targetItem) return null;

              // Find all markers for selected item
              const selectedItemMarkers = mapMarkers.filter(
                (m) => m.listingItemId === selectedItem.id
              );
              // Find all markers for target item
              const targetMarkers = mapMarkers.filter(
                (m) => m.listingItemId === targetItem.id
              );

              // Create gradients for each arc
              return selectedItemMarkers.flatMap(
                (selectedMarker, markerIndex) =>
                  targetMarkers.map((targetMarker, targetIndex) => {
                    const gradientId = `arc-gradient-${index}-${markerIndex}-${targetIndex}`;
                    const color = getItemTypeColor(targetItem.itemType);

                    // Calculate actual screen coordinates for gradient direction
                    const start = mapRef.current?.project([
                      selectedMarker.coordinates.lng,
                      selectedMarker.coordinates.lat,
                    ]);
                    const end = mapRef.current?.project([
                      targetMarker.coordinates.lng,
                      targetMarker.coordinates.lat,
                    ]);

                    if (!start || !end) return null;

                    return (
                      <linearGradient
                        key={gradientId}
                        id={gradientId}
                        x1={start.x}
                        y1={start.y}
                        x2={end.x}
                        y2={end.y}
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor={color} stopOpacity="1" />
                        <stop
                          offset="100%"
                          stopColor={color}
                          stopOpacity="0.3"
                        />
                      </linearGradient>
                    );
                  })
              );
            })}
          </defs>
          {selectedItem.connections.map((connection, index) => {
            const targetItem: ListingWithDetails =
              listingItemsDict[connection.id];
            if (!targetItem) return null;

            // Find all markers for selected item
            const selectedItemMarkers = mapMarkers.filter(
              (m) => m.listingItemId === selectedItem.id
            );
            // Find all markers for target item
            const targetMarkers = mapMarkers.filter(
              (m) => m.listingItemId === targetItem.id
            );

            // Draw lines from each selected marker to each target marker
            return selectedItemMarkers.flatMap((selectedMarker, markerIndex) =>
              targetMarkers.map((targetMarker, targetIndex) => {
                const start: Point | undefined = mapRef.current?.project([
                  selectedMarker.coordinates.lng,
                  selectedMarker.coordinates.lat,
                ]);
                const end: Point | undefined = mapRef.current?.project([
                  targetMarker.coordinates.lng,
                  targetMarker.coordinates.lat,
                ]);

                if (!start || !end) return null;

                return (
                  <MapArcPath
                    key={`${index}-${markerIndex}-${targetIndex}`}
                    start={start}
                    end={end}
                    gradientId={`arc-gradient-${index}-${markerIndex}-${targetIndex}`}
                    strokeWidth={2.5}
                  />
                );
              })
            );
          })}
        </svg>
      )}
    </div>
  );
};

export default Map;
